import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip, Upload, FolderOpen, FileText } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { trpc } from '@/lib/trpc';
import type { AttachedContextFile } from '../../../../shared/types/brain';

const MAX_FILES = 3;
const MAX_SIZE_BYTES = 500 * 1024; // 500 KB

interface FilePickerProps {
  files: AttachedContextFile[];
  onAdd: (file: AttachedContextFile) => void;
  onRemove: (key: string) => void;
  onUpload: (file: File) => Promise<void>;
  disabled?: boolean;
}

export function FilePicker({ files, onAdd, onUpload, disabled }: FilePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'upload' | 'existing'>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: existingData, isLoading } = trpc.brain.listContextFiles.useQuery(
    undefined,
    { enabled: open && tab === 'existing' }
  );
  const existingFiles = existingData?.files ?? [];

  const canAddMore = files.length < MAX_FILES;

  async function handleDrop(rawFile: File) {
    if (!canAddMore) return;
    if (rawFile.size > MAX_SIZE_BYTES) {
      alert(t('brain.file_picker.size_limit_error'));
      return;
    }
    if (!rawFile.name.match(/\.(pdf|txt|md)$/i)) {
      alert(t('brain.file_picker.type_error'));
      return;
    }
    setUploading(true);
    try {
      await onUpload(rawFile);
    } finally {
      setUploading(false);
    }
  }

  function selectExisting(f: { key: string; filename: string }) {
    if (!canAddMore) return;
    if (files.find((x) => x.key === f.key)) return;
    onAdd({ key: f.key, filename: f.filename });
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={disabled || !canAddMore}
          title={canAddMore ? t('brain.file_picker.attach_btn_title') : t('brain.file_picker.max_files_title')}
          className={`flex items-center justify-center w-8 h-8 rounded-lg border transition-colors disabled:opacity-40 ${
            files.length > 0
              ? 'border-blue-400/40 bg-blue-500/10 text-blue-500 hover:bg-blue-500/20'
              : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          <Paperclip className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-72 p-0 shadow-lg">
        {/* Tabs */}
        <div className="flex border-b border-border">
          {(['upload', 'existing'] as const).map((tabKey) => (
            <button
              key={tabKey}
              onClick={() => setTab(tabKey)}
              className={`flex-1 py-2 text-xs font-medium transition-colors ${
                tab === tabKey
                  ? 'text-primary border-b-2 border-primary bg-primary/5'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tabKey === 'upload' ? t('brain.file_picker.tab_upload') : t('brain.file_picker.tab_my_files')}
            </button>
          ))}
        </div>

        {tab === 'upload' && (
          <div className="p-3 flex flex-col gap-2">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={async (e) => {
                e.preventDefault();
                setDragOver(false);
                const file = e.dataTransfer.files[0];
                if (file) { await handleDrop(file); setOpen(false); }
              }}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-2 h-28 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                uploading ? 'opacity-60 pointer-events-none' :
                dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
              }`}
            >
              {uploading ? (
                <svg className="w-5 h-5 animate-spin text-primary" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : (
                <Upload className="w-5 h-5 text-muted-foreground" />
              )}
              <p className="text-xs text-muted-foreground text-center">
                {uploading ? t('brain.file_picker.uploading') : (
                  <>{t('brain.file_picker.upload_prompt')}<br /><span className="text-[11px]">{t('brain.file_picker.upload_hint')}</span></>
                )}
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.txt,.md"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) { await handleDrop(file); setOpen(false); }
              }}
            />
            <p className="text-[11px] text-muted-foreground text-center">
              {t('brain.file_picker.file_count', { count: files.length, max: MAX_FILES })}
            </p>
          </div>
        )}

        {tab === 'existing' && (
          <div className="max-h-56 overflow-y-auto">
            {isLoading && (
              <p className="text-xs text-muted-foreground text-center py-6">{t('brain.file_picker.loading')}</p>
            )}
            {!isLoading && existingFiles.length === 0 && (
              <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                <FolderOpen className="w-8 h-8 opacity-40" />
                <p className="text-xs">{t('brain.file_picker.no_files')}</p>
              </div>
            )}
            {existingFiles.map((f) => {
              const alreadyAdded = !!files.find((x) => x.key === f.key);
              return (
                <button
                  key={f.key}
                  disabled={alreadyAdded || !canAddMore}
                  onClick={() => selectExisting(f)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs truncate">{f.filename}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {(f.size / 1024).toFixed(0)} KB
                    </p>
                  </div>
                  {alreadyAdded && (
                    <span className="text-[10px] text-primary">{t('brain.file_picker.already_added')}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
